// src/pages/Admin/Dojos/SettingsDojos.jsx
import React, { useEffect, useState } from "react";
import {
  getDojos,
  deleteDojo,
  updateOrderDojo,
  getGroupsByDojo,
} from "../../../services/dojoServices";
import Button from "../../../components/Button/Button";
import ButtonEdit from "../../../components/Button/ButtonEdit";
import ButtonDelete from "../../../components/Button/ButtonDelete";
import { FaPlus } from "react-icons/fa";
import { TbTriangleFilled, TbTriangleInvertedFilled } from "react-icons/tb";
import { ToastContainer, toast } from "react-toastify";
import CreateDojoModal from "./CreateDojoModal";
import EditDojoModal from "./EditDojoModal";
import { toAbsolutePhotoUrl } from "../../../services/dojoPhotoServices";

const NO_IMAGE = "/img/no-image.jpg";

const SettingsDojos = () => {
  const [dojos, setDojos] = useState([]);
  const [loading, setLoading] = useState(true);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedDojo, setSelectedDojo] = useState(null);

  useEffect(() => {
    fetchDojos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDojos = async () => {
    setLoading(true);
    try {
      const data = await getDojos();
      setDojos(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Erreur lors de la récupération des dojos", error);
      toast.error("Impossible de charger les dojos.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDojo = async (dojo) => {
    try {
      const groupes = await getGroupsByDojo(dojo.id);
      if (Array.isArray(groupes) && groupes.length > 0) {
        const groupeNames = groupes.map((g) => g.nom).join(", ");
        toast.error(
          `Impossible de supprimer « ${dojo.dojo_nom} », des groupes y sont rattachés : ${groupeNames}.`
        );
        return;
      }

      if (
        window.confirm(
          `Êtes-vous sûr de vouloir supprimer le dojo « ${dojo.dojo_nom} » ?`
        )
      ) {
        await deleteDojo(dojo.id);
        toast.success("Dojo supprimé avec succès");
        fetchDojos();
      }
    } catch (error) {
      console.error("Erreur lors de la suppression du dojo :", error);
      toast.error(
        "Erreur lors de la vérification des groupes ou de la suppression."
      );
    }
  };

  const openEditModal = (dojo) => setSelectedDojo(dojo);
  const closeEditModal = () => setSelectedDojo(null);

  const moveDojoUp = async (index) => {
    if (index === 0) return;
    const newDojos = [...dojos];
    const temp = newDojos[index - 1];
    newDojos[index - 1] = newDojos[index];
    newDojos[index] = temp;
    newDojos[index - 1].dojo_ordre--;
    newDojos[index].dojo_ordre++;
    setDojos(newDojos);
    await updateOrderDojo(newDojos);
    fetchDojos();
  };

  const moveDojoDown = async (index) => {
    if (index === dojos.length - 1) return;
    const newDojos = [...dojos];
    const temp = newDojos[index + 1];
    newDojos[index + 1] = newDojos[index];
    newDojos[index] = temp;
    newDojos[index + 1].dojo_ordre++;
    newDojos[index].dojo_ordre--;
    setDojos(newDojos);
    await updateOrderDojo(newDojos);
    fetchDojos();
  };

  return (
    <section className="w-full">
      <div className="mx-auto max-w-7xl px-1 sm:px-1">
        <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/70 dark:bg-slate-900/60 backdrop-blur">
          {/* HEADER */}
          <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-6 sm:px-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 className="text-white text-2xl sm:text-3xl font-bold tracking-tight">
                Gestion des Dojos
              </h2>
              <p className="text-white/90 text-sm sm:text-[15px] mt-1">
                Créez, modifiez et organisez vos dojos.
              </p>
            </div>
            <Button
              text="Créer un Dojo"
              icon={FaPlus}
              onClick={() => setIsCreateModalOpen(true)}
              className="!bg-white !text-slate-900 !hover:bg-white/90 !shadow-md !rounded-xl gap-2"
              hoverLabel="Créer un nouveau dojo"
            />
          </div>

          {/* CONTENT */}
          <div className="p-4 sm:p-6">
            {/* LOADING SKELETON */}
            {loading && (
              <div className="space-y-3">
                {[...Array(4)].map((_, i) => (
                  <div
                    key={i}
                    className="animate-pulse rounded-xl border border-slate-200 dark:border-slate-800 p-4 sm:p-5 flex items-center gap-4 bg-white/60 dark:bg-slate-900/40"
                  >
                    <div className="h-10 w-10 rounded-lg bg-slate-200 dark:bg-slate-700" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 w-2/5 bg-slate-200 dark:bg-slate-700 rounded" />
                      <div className="h-3 w-1/3 bg-slate-200 dark:bg-slate-700 rounded" />
                      <div className="h-3 w-1/4 bg-slate-200 dark:bg-slate-700 rounded" />
                    </div>
                    <div className="h-8 w-24 bg-slate-200 dark:bg-slate-700 rounded" />
                  </div>
                ))}
              </div>
            )}

            {/* EMPTY STATE */}
            {!loading && dojos.length === 0 && (
              <div className="text-center py-14">
                <div className="mx-auto h-16 w-16 rounded-2xl bg-gradient-to-br from-blue-500 via-violet-500 to-pink-500 opacity-80" />
                <h3 className="mt-6 text-lg font-semibold text-slate-800 dark:text-slate-200">
                  Aucun dojo pour le moment
                </h3>
                <p className="mt-1 text-slate-600 dark:text-slate-400">
                  Cliquez sur “Créer un Dojo” pour commencer.
                </p>
              </div>
            )}

            {/* DESKTOP TABLE */}
            {!loading && dojos.length > 0 && (
              <>
                <div className="hidden md:block">
                  <div className="overflow-hidden rounded-xl border border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/40">
                    <table
                      className="min-w-full
                      [&_thead_th]:text-slate-700 dark:[&_thead_th]:text-white"
                    >
                      <thead className="sticky top-0 z-10 bg-slate-50/90 dark:bg-slate-900/90 backdrop-blur">
                        <tr className="uppercase tracking-wide text-xs">
                          {["Photo", "Nom", "Discipline", "Adresse", "Ordre", "Actions"].map((h) => (
                            <th key={h} className="px-4 py-3 text-left font-semibold">
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                        {dojos.map((dojo, index) => {
                          const photoSrc =
                            toAbsolutePhotoUrl(dojo.dojo_photoURL?.trim() || "") || NO_IMAGE;
                          return (
                            <tr
                              key={dojo.id}
                              className="group transition-all duration-150 hover:bg-slate-50/70 dark:hover:bg-slate-800/40"
                            >
                              {/* Photo */}
                              <td className="px-4 py-3">
                                <img
                                  src={photoSrc}
                                  alt={`Dojo ${dojo.dojo_nom}`}
                                  className="h-20 w-20 rounded-lg object-cover"
                                  onError={(e) => {
                                    e.currentTarget.src = NO_IMAGE;
                                  }}
                                  referrerPolicy="no-referrer"
                                  crossOrigin="anonymous"
                                />
                              </td>
                              {/* Nom */}
                              <td className="px-4 py-3 text-sm font-semibold text-slate-900 dark:text-slate-100">
                                {dojo.dojo_nom}
                              </td>
                              {/* Discipline */}
                              <td className="px-4 py-3 text-sm text-slate-700 dark:text-slate-300">
                                {dojo.dojo_discipline || "—"}
                              </td>
                              {/* Adresse */}
                              <td className="px-4 py-3 text-sm text-slate-700 dark:text-slate-300">
                                {dojo.dojo_adresse || "—"}
                              </td>
                              {/* Ordre + flèches */}
                              <td className="px-4 py-3 text-sm text-slate-700 dark:text-slate-300">
                                <div className="inline-flex items-center gap-2">
                                  <span className="inline-flex h-8 min-w-[2rem] items-center justify-center rounded-md border border-slate-200 dark:border-slate-700 px-2 text-sm font-semibold text-slate-800 dark:text-slate-100 bg-white/70 dark:bg-slate-900/40">
                                    {dojo.dojo_ordre}
                                  </span>
                                  <div className="flex flex-col -my-1">
                                    <button
                                      onClick={() => moveDojoUp(index)}
                                      className="p-1 text-slate-500 hover:text-slate-800 dark:hover:text-white transition"
                                      title="Monter"
                                    >
                                      <TbTriangleFilled />
                                    </button>
                                    <button
                                      onClick={() => moveDojoDown(index)}
                                      className="p-1 text-slate-500 hover:text-slate-800 dark:hover:text-white transition"
                                      title="Descendre"
                                    >
                                      <TbTriangleInvertedFilled />
                                    </button>
                                  </div>
                                </div>
                              </td>
                              {/* Actions */}
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-2">
                                  <ButtonEdit onClick={() => openEditModal(dojo)} />
                                  <ButtonDelete onClick={() => handleDeleteDojo(dojo)} />
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* MOBILE CARDS */}
                <div className="md:hidden space-y-3">
                  {dojos.map((dojo, index) => {
                    const photoSrc =
                      toAbsolutePhotoUrl(dojo.dojo_photoURL?.trim() || "") || NO_IMAGE;
                    return (
                      <div
                        key={`card-${dojo.id}`}
                        className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/40 p-4 shadow-sm transition hover:shadow-md"
                      >
                        <div className="flex items-start gap-4">
                          <img
                            src={photoSrc}
                            alt={`Dojo ${dojo.dojo_nom}`}
                            className="shrink-0 h-10 w-10 rounded-lg object-cover"
                            onError={(e) => {
                              e.currentTarget.src = NO_IMAGE;
                            }}
                            referrerPolicy="no-referrer"
                            crossOrigin="anonymous"
                          />
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                                {dojo.dojo_nom}
                              </h3>
                              <div className="flex items-center gap-1">
                                <span className="inline-flex h-7 min-w-[2rem] items-center justify-center rounded-md border border-slate-200 dark:border-slate-700 px-2 text-sm font-semibold text-slate-800 dark:text-slate-100 bg-white/70 dark:bg-slate-900/40">
                                  {dojo.dojo_ordre}
                                </span>
                                <div className="flex flex-col -my-1">
                                  <button
                                    onClick={() => moveDojoUp(index)}
                                    className="p-1 text-slate-500 hover:text-slate-800 dark:hover:text-white transition"
                                    title="Monter"
                                  >
                                    <TbTriangleFilled />
                                  </button>
                                  <button
                                    onClick={() => moveDojoDown(index)}
                                    className="p-1 text-slate-500 hover:text-slate-800 dark:hover:text-white transition"
                                    title="Descendre"
                                  >
                                    <TbTriangleInvertedFilled />
                                  </button>
                                </div>
                              </div>
                            </div>

                            <p className="mt-1 text-sm text-slate-700 dark:text-slate-300">
                              {dojo.dojo_discipline || "—"}
                            </p>
                            <p className="text-xs text-slate-500 dark:text-slate-400">
                              {dojo.dojo_adresse || "—"}
                            </p>

                            <div className="mt-4 flex items-center gap-2">
                              <ButtonEdit onClick={() => openEditModal(dojo)} />
                              <ButtonDelete onClick={() => handleDeleteDojo(dojo)} />
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Modales */}
      {isCreateModalOpen && (
        <CreateDojoModal
          isActive={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          refreshDojos={fetchDojos}
        />
      )}

      {selectedDojo && (
        <EditDojoModal
          isActive={Boolean(selectedDojo)}
          onClose={closeEditModal}
          refreshDojos={fetchDojos}
          dojoData={selectedDojo}
        />
      )}

      <ToastContainer />
    </section>
  );
};

export default SettingsDojos;
